import { useCallback, useEffect, useRef, useState } from 'react';
import useDebounce from 'hooks/useDebounce';
import useContentAnalytics from 'hooks/useContentAnalytics';
import useClickOutsideDetect from 'hooks/useClickOutsideDetect';

type UseContentIdSelectorOptions = {
  isOpen: boolean;
  onClose: () => void;
};

export default function useContentIdSelector({ isOpen, onClose }: UseContentIdSelectorOptions) {
  const [searchVal, setSearchVal] = useState('');
  const debouncedSearch = useDebounce(searchVal, 300);
  const {
    contentInstances,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasMoreContentInstances,
    fetchNextContentPage,
    isError,
    serverErrorMessage,
  } = useContentAnalytics(debouncedSearch);

  const scopeRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [lastOptionElement, setLastOptionElement] = useState<HTMLElement | null>(null);

  const close = useCallback(() => {
    setSearchVal('');
    onClose();
  }, [onClose]);

  useClickOutsideDetect(scopeRef, () => {
    if (isOpen) close();
  });

  useEffect(() => {
    if (!isOpen || !menuRef.current || !lastOptionElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreContentInstances && !isFetchingNextPage) {
          fetchNextContentPage();
        }
      },
      {
        root: menuRef.current,
        threshold: 0.25,
      },
    );

    observer.observe(lastOptionElement);
    return () => observer.disconnect();
  }, [fetchNextContentPage, hasMoreContentInstances, isFetchingNextPage, isOpen, lastOptionElement]);

  return {
    scopeRef,
    menuRef,
    searchVal,
    setSearchVal,
    contentInstances,
    isLoading,
    isFetchingNextPage,
    isError,
    errorMessage: serverErrorMessage,
    setLastOptionElement,
    close,
  };
}
